import type { RealtimeContext, RealtimeEvent, RealtimeHandler } from '@/types/realtime';

type HandlerEntry = {
  id: string;
  topic: string;
  handler: RealtimeHandler;
};

const DEDUP_WINDOW_MS = 60_000;
const MAX_DEDUP_ENTRIES = 500;

export class RealtimeDispatcher {
  private handlers: HandlerEntry[] = [];
  private seenEventIds = new Map<string, number>();

  register(topic: string, id: string, handler: RealtimeHandler): () => void {
    const entry = { topic, id, handler };
    this.handlers = this.handlers.filter((item) => item.topic !== topic || item.id !== id);
    this.handlers.push(entry);
    return () => {
      this.handlers = this.handlers.filter((item) => item !== entry);
    };
  }

  async dispatch(event: RealtimeEvent, context: RealtimeContext): Promise<void> {
    if (!this.shouldEmit(event)) return;
    const handlers = this.handlers.filter((entry) => entry.topic === event.topic);
    await Promise.all(
      handlers.map(async (entry) => {
        try {
          await entry.handler(event, context);
        } catch (error) {
          console.warn('[realtime] handler failed', {
            topic: event.topic,
            event: event.event,
            handlerId: entry.id,
            error,
          });
        }
      })
    );
  }

  private shouldEmit(event: RealtimeEvent): boolean {
    if (!event.eventId) return true;
    const key = `${event.topic}:${event.event}:${event.eventId}`;
    const now = Date.now();
    this.prune(now);
    if (this.seenEventIds.has(key)) return false;
    this.seenEventIds.set(key, now + DEDUP_WINDOW_MS);
    return true;
  }

  private prune(now: number): void {
    for (const [key, expiresAt] of this.seenEventIds.entries()) {
      if (expiresAt <= now || this.seenEventIds.size > MAX_DEDUP_ENTRIES) {
        this.seenEventIds.delete(key);
      }
    }
  }
}

export const realtimeDispatcher = new RealtimeDispatcher();
